import type { ReactNode } from 'react';
import type { Message } from '@spawnwire/shared';
import { Avatar } from '../../components/ui';
import { Markdown } from '../../lib/markdown';
import { formatTimestamp } from '../../lib/time';
import { displayNameIn, roleColorOf, useStore } from '../../store/store';
import { useRenderContext } from './MessageItem';

/** Compact, read-only message card used in search results and pins. */
export function MessagePreview({ message, actions, context }: { message: Message; actions?: ReactNode; context?: ReactNode }) {
  const ctx = useRenderContext(message.spaceId);
  const state = useStore.getState();
  const name = message.author ? displayNameIn(state, message.author.id, message.spaceId) : 'Deleted User';
  const color = message.author ? roleColorOf(state, message.spaceId, message.author.id) : null;
  return (
    <div className="group rounded-lg border border-line bg-main p-3">
      {context && <div className="mb-1.5 text-xs text-faint">{context}</div>}
      <div className="flex gap-3">
        <Avatar user={message.author} size={32} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-semibold" style={color ? { color } : undefined}>
              {name}
            </span>
            <time className="shrink-0 text-[11px] text-faint" dateTime={message.createdAt}>
              {formatTimestamp(message.createdAt)}
            </time>
          </div>
          <div className="line-clamp-6 whitespace-pre-wrap break-words text-sm text-fg">
            <Markdown content={message.content} ctx={ctx} />
          </div>
          {message.attachments.length > 0 && (
            <div className="mt-1 text-xs text-muted">
              {message.attachments.length} attachment{message.attachments.length === 1 ? '' : 's'}: {message.attachments.map((a) => a.filename).join(', ')}
            </div>
          )}
        </div>
      </div>
      {actions && <div className="mt-2 flex justify-end gap-2">{actions}</div>}
    </div>
  );
}
